import {useMemo, useState} from 'react';
import {CustomerDetails, CustomerFileKPI, CustomerSystem} from '@/store/customerFile.ts';
import {
    BuildingOffice2Icon,
    UserGroupIcon,
    BanknotesIcon,
    CubeIcon,
    CreditCardIcon,
    ShoppingCartIcon,
    ArrowsRightLeftIcon,
    SignalIcon,
    ChatBubbleLeftRightIcon,
    UserPlusIcon,
    PlusCircleIcon,
} from '@heroicons/react/24/outline';
import {Link} from '@/layout/components/link.tsx';
import {TransferUnitsModal} from './modals/TransferUnitsModal.tsx';
import {BuyChannelsModal} from './modals/BuyChannelsModal.tsx';
import {BuyUnitsModal} from './modals/BuyUnitsModal.tsx';
import {InviteUserModal} from './modals/InviteUserModal.tsx';
import {EditBillingModal} from './EditBillingModal.tsx';
import {NewServiceCallModal} from '@/pages/service-request/components/NewServiceCallModal.tsx';
import {ConnectToSystemModal} from '@/modals/ConnectToSystem.tsx';
import type {CustomerFileTabValue} from './CustomerFileTabs.tsx';

interface Props {
    kpi: CustomerFileKPI;
    paymentDay: number;
    onTabSelect?: (tab: CustomerFileTabValue) => void;
}

function fmtCurrency(val: string | number | null | undefined) {
    if (val == null) return null;
    const n = parseFloat(String(val));
    return `₪\u00a0${n.toLocaleString('he-IL', {minimumFractionDigits: 0, maximumFractionDigits: 2})}`;
}

function fmtNum(val: number | null | undefined) {
    if (val == null) return '0';
    return val.toLocaleString('he-IL');
}

interface KPICardProps {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    subLine?: React.ReactNode;
    colorClass?: string;
    className?: string;
    onClick?: () => void;
}

function KPICard({icon, label, value, subLine, colorClass = 'text-blue-600 bg-blue-50', className = '', onClick}: KPICardProps) {
    const rootClassName = `bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3 w-full text-right ${onClick ? 'transition-colors hover:bg-gray-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300' : ''} ${className}`;

    if (onClick) {
        return (
            <button type="button" className={rootClassName} onClick={onClick}>
                <div className={`rounded-xl p-2.5 shrink-0 ${colorClass}`}>
                    <div className="w-5 h-5">{icon}</div>
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-gray-400 truncate mb-0.5">{label}</p>
                    <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
                    {subLine && <p className="text-[11px] text-gray-400 mt-1 leading-tight">{subLine}</p>}
                </div>
            </button>
        );
    }

    return (
        <div className={rootClassName}>
            <div className={`rounded-xl p-2.5 shrink-0 ${colorClass}`}>
                <div className="w-5 h-5">{icon}</div>
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[11px] text-gray-400 truncate mb-0.5">{label}</p>
                <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
                {subLine && <p className="text-[11px] text-gray-400 mt-1 leading-tight">{subLine}</p>}
            </div>
        </div>
    );
}

type QuickActionKey = 'pay-invoice' | 'buy-units' | 'transfer-units' | 'buy-channels' | 'open-ticket' | 'update-payment' | 'invite-user' | 'open-new-system';

interface QuickAction {label: string; shortLabel: string; icon: React.ReactNode; to?: string; key: QuickActionKey}
const QUICK_ACTIONS: QuickAction[] = [
    // {key: 'pay-invoice', label: 'תשלום חשבונית פתוחה', shortLabel: 'שלם חשבונית', icon: <CreditCardIcon className="w-5 h-5" />, to: '/system/account'},
    {key: 'buy-units', label: 'רכישת יחידות', shortLabel: 'רכישת יחידות', icon: <ShoppingCartIcon className="w-5 h-5" />},
    {key: 'transfer-units', label: 'העברת יחידות', shortLabel: 'העברת יחידות', icon: <ArrowsRightLeftIcon className="w-5 h-5" />},
    {key: 'buy-channels', label: 'רכישת ערוצים', shortLabel: 'רכישת ערוצים', icon: <SignalIcon className="w-5 h-5" />},
    {key: 'open-ticket', label: 'פתיחת פניה', shortLabel: 'פתיחת פניה', icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />},
    {key: 'update-payment', label: 'עדכון אמצעי תשלום', shortLabel: 'עדכון תשלום', icon: <CreditCardIcon className="w-5 h-5" />},
    {key: 'invite-user', label: 'הזמנת משתמש', shortLabel: 'הזמנת משתמש', icon: <UserPlusIcon className="w-5 h-5" />},
    {key: 'open-new-system', label: 'פתיחת מערכת חדשה', shortLabel: 'מערכת חדשה', icon: <PlusCircleIcon className="w-5 h-5" />},
];

interface QuickActionsCardProps {
    systems: CustomerSystem[];
    customer: CustomerDetails;
    currentSystemId?: number | null;
    currentUserRole?: number;
}

export function QuickActionsCard({systems, customer, currentSystemId, currentUserRole}: QuickActionsCardProps) {
    const [transferOpen, setTransferOpen] = useState(false);
    const [buyChannelsOpen, setBuyChannelsOpen] = useState(false);
    const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);
    const [serviceCallOpen, setServiceCallOpen] = useState(false);
    const [inviteUserOpen, setInviteUserOpen] = useState(false);
    const [editBillingOpen, setEditBillingOpen] = useState(false);
    const [connectSystemOpen, setConnectSystemOpen] = useState(false);

    const selectedSystem = useMemo(() => {
        if (systems.length === 0) return null;
        return systems.find((s) => s.id === currentSystemId) ?? systems.find((s) => s.active) ?? systems[0];
    }, [systems, currentSystemId]);

    function handleActionClick(action: QuickAction) {
        switch (action.key) {
            case 'buy-units':
                setBuyUnitsOpen(true);
                return;
            case 'transfer-units':
                if (selectedSystem) setTransferOpen(true);
                return;
            case 'buy-channels':
                if (selectedSystem) setBuyChannelsOpen(true);
                return;
            case 'open-ticket':
                setServiceCallOpen(true);
                return;
            case 'update-payment':
                setEditBillingOpen(true);
                return;
            case 'invite-user':
                setInviteUserOpen(true);
                return;
            case 'open-new-system':
                setConnectSystemOpen(true);
                return;
            default:
                return;
        }
    }

    return (
        <>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-1">
                <p className="text-xs font-semibold text-gray-400 tracking-wide">פעולות נפוצות</p>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                    {QUICK_ACTIONS.map((action) => {
                        const hasLink = Boolean(action.to);
                        const isExternal = hasLink && action.to!.startsWith('http');
                        const disabledForMissingSystem = (action.key === 'transfer-units' || action.key === 'buy-channels') && !selectedSystem;
                        const inner = (
                            <button
                                type={hasLink ? undefined : 'button'}
                                className="flex flex-col items-center gap-1.5 text-center rounded-xl p-2 hover:bg-violet-50 w-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                title={action.label}
                                onClick={() => !hasLink && handleActionClick(action)}
                                disabled={disabledForMissingSystem}
                            >
                                <span className="text-violet-600 bg-violet-50 rounded-lg p-1.5 group-hover:bg-violet-100 transition-colors">
                                    {action.icon}
                                </span>
                                <span className="text-[11px] text-gray-600 leading-tight">{action.shortLabel}</span>
                            </button>
                        );

                        if (hasLink && isExternal) {
                            return (
                                <a key={action.label} href={action.to} target="_blank" rel="noreferrer" className="group">
                                    {inner}
                                </a>
                            );
                        }
                        if (hasLink) {
                            return (
                                <Link key={action.label} to={action.to!} className="group">
                                    {inner}
                                </Link>
                            );
                        }

                        return (
                            <div key={action.label} className="group">
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedSystem && (
                <>
                    <TransferUnitsModal open={transferOpen} onClose={() => setTransferOpen(false)} fromSystem={selectedSystem} />
                    <BuyChannelsModal open={buyChannelsOpen} onClose={() => setBuyChannelsOpen(false)} system={selectedSystem} />
                </>
            )}

            {selectedSystem && (
                <BuyUnitsModal open={buyUnitsOpen} onClose={() => setBuyUnitsOpen(false)} systems={systems} customer={customer} currentSystemId={selectedSystem?.id} />
            )}

            <NewServiceCallModal open={serviceCallOpen} onOpenChange={setServiceCallOpen} />
            <InviteUserModal open={inviteUserOpen} systems={systems} currentUserRole={currentUserRole} onClose={() => setInviteUserOpen(false)} />
            <EditBillingModal open={editBillingOpen} onClose={() => setEditBillingOpen(false)} customer={customer} />
            <ConnectToSystemModal state={[connectSystemOpen, setConnectSystemOpen]} newOnly>
                <span className="hidden" aria-hidden="true" />
            </ConnectToSystemModal>
        </>
    );
}

export function CustomerFileKPIs({kpi, onTabSelect}: Props) {
    const monthlyCharge = fmtCurrency(kpi.monthly_charge);
    const prevCharge = kpi.monthly_charge_prev ? fmtCurrency(kpi.monthly_charge_prev) : null;
    const pendingCountBad = kpi.pending_invoices_count > 0;

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* 1 - מערכות */}
            <KPICard
                icon={<BuildingOffice2Icon />}
                label="מערכות פעילות"
                value={fmtNum(kpi.systems_count)}
                colorClass="text-blue-600 bg-blue-50"
                onClick={() => onTabSelect?.('systems')}
            />

            {/* 2 - משתמשים */}
            <KPICard
                icon={<UserGroupIcon />}
                label="משתמשים רשומים"
                value={fmtNum(kpi.users_count)}
                colorClass="text-teal-600 bg-teal-50"
                onClick={() => onTabSelect?.('users')}
            />

            {/* 3 - חיובים */}
            <KPICard
                icon={<BanknotesIcon />}
                label="חיובים"
                value={monthlyCharge ?? '—'}
                subLine={
                    prevCharge && kpi.monthly_charge_prev_label
                        ? <>{prevCharge} <span className="text-gray-400">חויב ב{kpi.monthly_charge_prev_label}</span></>
                        : undefined
                }
                colorClass="text-emerald-600 bg-emerald-50"
                onClick={() => onTabSelect?.('invoices')}
            />

            {/* 4 - יחידות */}
            <KPICard
                icon={<CubeIcon />}
                label="יחידות"
                value={fmtNum(kpi.units_balance)}
                subLine={
                    kpi.units_used_prev > 0
                        ? <>{fmtNum(kpi.units_used_prev)} <span className="text-gray-400">נוצלו ב{kpi.units_prev_label}</span></>
                        : <span className="text-gray-400">יתרה כוללת</span>
                }
                colorClass={pendingCountBad ? 'text-amber-600 bg-amber-50' : 'text-indigo-600 bg-indigo-50'}
                onClick={() => onTabSelect?.('units')}
            />

        </div>
    );
}
